import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useIsLoyaltyOfferInstanceActive } from "../../../../services/hooks/loyalty/useLoyaltyOfferInstanceActive";
import { usePayWithPoints } from "../../../../services/hooks/loyalty/usePayWithPoints";
import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useLoyaltyUiModel } from "../../../../services/hooks/useLoyaltyUiModel";
import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { selectLoyaltyFlashOffers } from "../../../../services/redux/selectors/loyaltyOfferSelectors";
import {
  selectGuestMilestonesEnabled,
  selectGuestTiersEnabled,
  selectHasAnyTierMilestones,
  selectUseLoyaltyService,
} from "../../../../services/redux/selectors/loyaltyServiceSelectors";
import { TransformOfferTypeObject } from "../../../../services/redux/types/TypeHelper";
import {
  CUSTOMER_ANALYTICS_EVENT,
  ContainerStyle,
  OfferCardSize,
  PagesEnum,
} from "../../../../utils/enums";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import { groupByPoints } from "../../../../utils/help/offers/offers";
import { buildLoyaltyCardViewModel } from "../../../../utils/loyalty/buildLoyaltyCardViewModel";
import {
  getCurrentLoyaltyTier,
  getTierColor,
  sortLoyaltyTiers,
  toTierCssProperties,
} from "../../../../utils/loyalty/tiers";
import Offer from "../../../../utils/OfferClass/Offer/Offer";
import {
  INVERTED_SECTIONS,
  OFFER_SYSTEM,
  RestaurantPackageOffer,
} from "../../../../utils/types";
import { TermsAndConditionsDrawerComponent } from "../../../global/Drawer/TermsAndConditionsDrawerComponent";
import FlashOfferBanner from "../../../global/FlashOfferBanner/FlashOfferBanner";
import { GeneralOfferIcon } from "../../../global/GeneralOfferIcon/GeneralOfferIcon";
import { TierArrowIcon } from "../../../global/Icons/loyalty.icons";
import LoyaltyFlashOfferBanner from "../../../global/LoyaltyFlashOfferBanner/LoyaltyFlashOfferBanner";
import { PayWithPointsSection } from "../../../global/PayWithPoints/PayWithPointsSection";
import { ReferAFriend } from "../../../global/ReferAFriend/ReferAFriend";
import { LoyaltyOfferCard } from "../../S-Cart/CartScreenOfferSection/OfferCard/LoyaltyOfferCard/LoyaltyOfferCard";
import { LoyaltyTierCarousel } from "../LoyaltyTiers/LoyaltyTierCarousel/LoyaltyTierCarousel";
import { LoyaltyTierRewardsSummary } from "../LoyaltyTiers/LoyaltyTierRewardsSummary/LoyaltyTierRewardsSummary";
import SelectOrderBanner from "../SelectOrderBanner/SelectOrderBanner";
import { LegacyLoyaltyPointsHeader } from "./LegacyLoyaltyPointsHeader/LegacyLoyaltyPointsHeader";
import { LegacyOfferCards } from "./LegacyOfferCards/LegacyOfferCards";
import styles from "./LoyaltyPointsLoyaltySection.module.scss";

/** Reward cards that have to be in the row before it offers "View all". */
const VIEW_ALL_CARD_MIN_REWARDS = 4;

export const LoyaltyPointsLoyaltySection = (props: { user: string }) => {
  const { user } = props;
  const { t, i18n } = useTranslationI18();
  const pointsText = t("pointing-system:point");
  const { displayPoints, displayPointsPerCredit, pointName } =
    useLoyaltyUiModel(pointsText, i18n);

  const currency = useAppSelector((state) => state.config.currency);
  const phoneNumberInfo = useAppSelector(
    (state) => state.userInfo.phoneNumberInfo
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const loyaltyState = useAppSelector((state) => state.cartAndLoyaltyReducer);
  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);

  const loyaltyInfoState = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );

  const loyaltyTierSystem = useAppSelector(
    (state) => state.loyaltyTierSystem.data
  );

  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const guestTiersEnabled = useAppSelector(selectGuestTiersEnabled);
  const guestMilestonesEnabled = useAppSelector(selectGuestMilestonesEnabled);
  const hasAnyTierMilestones = useAppSelector(selectHasAnyTierMilestones);
  const tiers = sortLoyaltyTiers(loyaltyTierSystem?.tiers);
  const currentTier = getCurrentLoyaltyTier(
    loyaltyInfoState,
    loyaltyTierSystem
  );
  const tierColor =
    guestTiersEnabled && currentTier
      ? getTierColor(currentTier, tiers)
      : undefined;
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const isOfferInstanceActive = useIsLoyaltyOfferInstanceActive();
  const { isPayWithPointsMechanism } = usePayWithPoints("browse");
  const isSignedIn = Boolean(phoneNumberInfo.isValid || user.length > 0);

  const flashOffers = loyaltyState.offersArray.filter(
    (offer) => offer.offerSystem !== OFFER_SYSTEM.POINTING_OFFER
  );
  const loyaltyFlashOffers = useAppSelector(selectLoyaltyFlashOffers);

  const pointingSystem = loyaltyState.pointingSystem;

  const currentOffer = loyaltyState.offersArray.find((o) =>
    o.isEqual(cartLoyaltyV2.cart.redeemedOffer)
  );
  const drawerState = useAppSelector(
    (state) =>
      state.globalComponentsToggleSlice.loyaltyTermsAndConditionsDrawer
        .isOpen || false
  );
  const referralCode = useAppSelector((state) => state.userInfo.referralCode);

  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const sliderRef = useRef<HTMLDivElement>(null);

  const totalBalanceText = t("pointing-system:total-balance");
  const collectText = t("pointing-system:collect");
  const forEveryText = t("pointing-system:for-every");
  const youSpendText = t("pointing-system:you-spend");
  const rewardsText = t("pointing-system:rewards");
  const viewIfNotSignInText = t("pointing-system:check-your-total");
  const viewTermsI18 = t("pointing-system:view-terms-and-conditions");

  const flashOffersText = t("pointing-system:flash-offers");
  const viewAllText = t("pointing-system:view-all");
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();

  const goToTerms = () => {
    router.push(`${getPageLink(router, PagesEnum.OurPolicies).as}`);
  };

  const goToAllRewards = () => {
    addCustomerAnalytics(
      CUSTOMER_ANALYTICS_EVENT.VIEW_ALL_REWARDS_FROM_REWARDS_PAGE
    );
    // const currentPath = router.asPath;
    // const newPath = currentPath.replace(PagesEnum.Home, PagesEnum.AllRewards);
    // router.replace(newPath);
    // href AND as: the loader prefetches the pair, and pushing both lets Next
    // reuse that entry instead of resolving the route again.
    const link = getPageLink(router, PagesEnum.AllRewards);

    router.push(link.href, link.as);
  };

  const showReferralCodeBanner =
    referralCode.rewardedPoints > 0 && !referralCode.promoCode.expired;

  const showFlashOfferBanners =
    flashOffers.length && selectedFranchise && orderTypeParam;
  const showLoyaltyFlashOfferBanners =
    useLoyaltyService &&
    !!loyaltyFlashOffers?.length &&
    selectedFranchise &&
    orderTypeParam;

  const showSelectOrderDataBanner = !selectedFranchise || !orderTypeParam;

  const [pointingOffers, setPointingOffers] = useState<
    TransformOfferTypeObject<RestaurantPackageOffer>[] | undefined
  >([]);

  useEffect(() => {
    const finalSortedOffers = groupByPoints(
      cartLoyaltyV2,
      itemStatusRedux,
      restaurantInfo?.timeZone!,
      orderTypeParam,
      pointingSystem?.restaurantPackageOffers || []
    ).flat() as TransformOfferTypeObject<RestaurantPackageOffer>[];

    if (currentOffer) {
      const sortedOffers = [...(finalSortedOffers || [])];
      const redeemedOfferIndex = sortedOffers.findIndex((offer) =>
        offer.packageOffer.isEqual(currentOffer)
      );
      if (redeemedOfferIndex > -1) {
        const [redeemedOffer] = sortedOffers.splice(redeemedOfferIndex, 1);
        sortedOffers.unshift(redeemedOffer);
      }
      setPointingOffers(sortedOffers);
    } else {
      setPointingOffers(finalSortedOffers);
    }

    if (sliderRef.current) {
      const firstOffer = sliderRef.current.querySelector("*");
      if (firstOffer) {
        firstOffer.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "end",
        });
      }
    }
  }, [currentOffer?.id]);

  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  const loyaltyMilestoneCards = useLoyaltyService
    ? buildLoyaltyCardViewModel({
        customerInfo: loyaltyInfoState,
        tierSystem: loyaltyTierSystem,
        pointName: pointsText,
        locale: i18n.language,
      }).milestones.flatMap((milestone) =>
        milestone.offerInstances.map((offer, offerIndex) => (
          <LoyaltyOfferCard
            // Index-qualified: split milestone offers repeat the same
            // offer id (one card per item choice, same underlying offer).
            key={`${milestone.milestoneId}-${offer.id}-${offerIndex}`}
            offer={offer}
            isActiveOffer={isOfferInstanceActive(offer)}
            size={OfferCardSize.medium}
            pointsToUnlock={milestone.pointsToUnlock}
            tierSurface={guestTiersEnabled}
          />
        ))
      )
    : null;

  const legacyOfferCards = useLoyaltyService
    ? null
    : pointingOffers?.filter(
        (resOffer: any) =>
          resOffer?.packageOffer &&
          typeof resOffer.packageOffer.shouldHideOffer === "function" &&
          !resOffer.packageOffer.shouldHideOffer(
            itemStatusRedux.disabledItemsIds
          )
      ) ?? null;

  const rewardCardCount = useLoyaltyService
    ? loyaltyMilestoneCards?.length ?? 0
    : legacyOfferCards?.length ?? 0;
  // An empty slider still contributes its own block padding, which showed up as
  // dead space between the rewards meter and the flash offers.
  const hasRewardCards = rewardCardCount > 0;
  /**
   * The card at the end of the row waits for the 4th reward rather than showing
   * from the beginning (prepits/project#4107, point 3): with a handful of cards
   * every reward is already on screen to scroll to. It is the one you reach by
   * scrolling the row itself, so it is not gated on being signed out either -
   * that left a member who scrolled past the 4th reward with nothing there.
   *
   * The summary's own "View all" is deliberately not gated on the count: it sits
   * beside the meter and stays there whatever the row holds.
   */
  const showViewAllCard =
    guestTiersEnabled && rewardCardCount >= VIEW_ALL_CARD_MIN_REWARDS;
  /**
   * Once the shortcut is there the row shows only the first four rewards and
   * leaves the rest to the All Rewards page (prepits/project#4107, point 3):
   * it is a preview, not the whole catalogue, so it should not run on past the
   * card that opens it.
   */
  const visibleMilestoneCards =
    loyaltyMilestoneCards && showViewAllCard
      ? loyaltyMilestoneCards.slice(0, VIEW_ALL_CARD_MIN_REWARDS)
      : loyaltyMilestoneCards;

  return (
    <>
      {drawerState && <TermsAndConditionsDrawerComponent />}
      {guestTiersEnabled ? (
        <LoyaltyTierCarousel />
      ) : (
        <LegacyLoyaltyPointsHeader
          isMainDark={isMainDark}
          totalBalanceText={totalBalanceText}
          isSignedIn={isSignedIn}
          displayPoints={displayPoints}
          pointName={pointName}
          signedOutText={viewIfNotSignInText}
          collectText={collectText}
          displayPointsPerCredit={displayPointsPerCredit}
          forEveryText={forEveryText}
          currency={currency}
          youSpendText={youSpendText}
          termsText={viewTermsI18}
          onViewTerms={() =>
            router.push(getPageLink(router, PagesEnum.OurPolicies).as)
          }
          isPayWithPointsMechanism={isPayWithPointsMechanism}
        />
      )}
      <PayWithPointsSection onHowItWorks={goToTerms} />

      <div
        className={`${styles.rewards_div} ${
          guestTiersEnabled ? styles.rewards_div_tiered : ""
        }`}
        data-testid="pointing-offers"
        style={tierColor ? toTierCssProperties(tierColor) : undefined}
      >
        {useLoyaltyService &&
        isPayWithPointsMechanism &&
        !guestMilestonesEnabled ? null : guestTiersEnabled ? (
          <LoyaltyTierRewardsSummary
            // Nothing configured to redeem means "View all" would land on an
            // empty page and the meter would have no milestones to plot.
            showMilestones={
              guestMilestonesEnabled && hasRewardCards && hasAnyTierMilestones
            }
            isSignedIn={isSignedIn}
            tierColor={tierColor}
            onViewAll={goToAllRewards}
          />
        ) : (
          <div className={styles.rewards_title}>
            <h1 className={styles.title}>{rewardsText}</h1>
            <button onClick={goToAllRewards} data-testid={`Redeem-button`}>
              {viewAllText}
            </button>
          </div>
        )}
        {!selectedFranchise && (
          <div
            className={styles.rewards_warning_text}
            data-testid="rewards-warning-text"
          >
            <span>{t("common:rewards-warning-text")}</span>
          </div>
        )}

        {(!guestTiersEnabled || guestMilestonesEnabled) &&
          (hasRewardCards || showViewAllCard) && (
            <div
              ref={sliderRef}
              className={`${styles.milestones} ${styles.slider}`}
              data-testid="pointing-offers-cards"
            >
              {useLoyaltyService
                ? visibleMilestoneCards
                : legacyOfferCards && (
                    <LegacyOfferCards
                      offers={legacyOfferCards}
                      cartLoyaltyV2={cartLoyaltyV2}
                      itemStatusRedux={itemStatusRedux}
                      orderTypeParam={orderTypeParam}
                      restaurantInfo={restaurantInfo}
                      sourceOffers={pointingSystem?.restaurantPackageOffers}
                      currentOffer={currentOffer}
                      displayPoints={displayPoints}
                    />
                  )}
              {showViewAllCard && (
                <button
                  type="button"
                  className={styles.view_all_card}
                  onClick={goToAllRewards}
                >
                  <span aria-hidden="true">
                    <TierArrowIcon />
                  </span>
                  {viewAllText}
                </button>
              )}
            </div>
          )}

        {showReferralCodeBanner ||
        flashOffers.length ||
        showLoyaltyFlashOfferBanners ? (
          <div style={{ backgroundColor: "white" }}>
            <div
              className={`${styles.flash_offers_wrapper} ${
                restaurantInfo?.colors?.invertedSections?.includes(
                  INVERTED_SECTIONS.FLASH_OFFERS
                )
                  ? "invert"
                  : ""
              }`}
            >
              {showLoyaltyFlashOfferBanners || flashOffers.length ? (
                <h3 className={styles.title}>{flashOffersText}</h3>
              ) : null}

              <div className={styles.offers}>
                {showReferralCodeBanner ? (
                  <ReferAFriend containerStyle={ContainerStyle.BOXED} />
                ) : null}

                {showLoyaltyFlashOfferBanners
                  ? loyaltyFlashOffers!.map((offer) => (
                      <LoyaltyFlashOfferBanner
                        key={offer.id}
                        offer={offer}
                        tierSized={guestTiersEnabled}
                      />
                    ))
                  : showFlashOfferBanners
                  ? flashOffers.map((offer: Offer, index) => (
                      <FlashOfferBanner
                        key={index}
                        offer={offer}
                        offersTab
                        tierSized={guestTiersEnabled}
                      />
                    ))
                  : showSelectOrderDataBanner && (
                      <SelectOrderBanner
                        bgColor="loyalty"
                        bannerText={t("common:order-info-banner")}
                        icon={
                          <GeneralOfferIcon backgroundIconColor="loyalty" />
                        }
                      />
                    )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};
